import React, { FC, useState } from 'react';
import { Alert, Button, Table } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import { useQuery } from 'react-query';

import ActionLink from '../../../components/ActionLink/ActionLink';
import FAIcon from '../../../components/FAIcon/FAIcon';
import Loading from '../../../components/Loading/Loading';
import { assetConst } from '../../../constants/assetConstants';
import { useReferenceData, useUserProfile } from '../../../hooks/reduxHooks';
import { getProductAssets } from '../../../services/productsService';
import { makeKey } from '../../../utils/componentUtils';
import { formatLocale } from '../../../utils/localeUtils';
import { canAddAssets } from '../ProductProfile.helpers';
import { compareDates, dateFormat, formatDate, parseDateString } from '../../../utils/dateUtils';
import { formatAssetType } from '../../../utils/assetUtils';
import { displayString } from '../../../utils/stringUtils';
import { isAuthorizedForPresignedUrl } from '../../../utils/userUtils';
import AssetUploadModal from '../modals/AssetUploadModal';
import StatusText from '../../../components/StatusText/StatusText';

import asset_no_preview from '../../../images/asset-nopreview.png';

type AssetType = (typeof assetConst.TYPE)[keyof typeof assetConst.TYPE];
const sortAssets = (
	a: { asset_type?: AssetType; submitted_date?: string },
	b: { asset_type?: AssetType; submitted_date?: string },
) => {
	if (a.asset_type && b.asset_type) {
		const { LABEL, SWITCH_DLC_INSERT, TITLE_SHEET, SWITCH2_DLC_INSERT } = assetConst.TYPE;
		if (a.asset_type === LABEL && b.asset_type !== LABEL) {
			return -1;
		}
		if (a.asset_type !== LABEL && b.asset_type === LABEL) {
			return 1;
		}
		if (a.asset_type === TITLE_SHEET && b.asset_type !== TITLE_SHEET) {
			return -1;
		}
		if (a.asset_type !== TITLE_SHEET && b.asset_type === TITLE_SHEET) {
			return 1;
		}
		const dlcInserts = [SWITCH_DLC_INSERT, SWITCH2_DLC_INSERT] as string[];
		if (dlcInserts.includes(a.asset_type) && !dlcInserts.includes(b.asset_type)) {
			return -1;
		}
		if (!dlcInserts.includes(a.asset_type) && dlcInserts.includes(b.asset_type)) {
			return 1;
		}
	}

	if (a.submitted_date || b.submitted_date) {
		const compared = compareDates(a.submitted_date || 0, b.submitted_date || 0, false);
		if (compared !== 0) return compared * -1;
	}

	if ((a.asset_type || '') < (b.asset_type || '')) return -1;
	if ((a.asset_type || '') > (b.asset_type || '')) return 1;
	return 0;
};

interface AssetsTabProps {
	isSelected: boolean;
	product: ProductData;
	readOnly: boolean;
	category: typeof assetConst.CATEGORY[keyof typeof assetConst.CATEGORY];
}
interface UploadModalModel {
	type?: 'ASSET_UPLOAD_MODAL';
	productId?: number;
	category?: typeof assetConst.CATEGORY[keyof typeof assetConst.CATEGORY];
	isSubmitting?: boolean;
}
const AssetsTab: FC<AssetsTabProps> = ({ isSelected, product, readOnly, category }) => {
	const userProfile = useUserProfile();
	const assetsQuery = useQuery(
		['getProductAssets', product?.product_id, category],
		() => getProductAssets(String(product?.product_id), category),
		{ enabled: product && isSelected }
	);
	const assetsOutput = assetsQuery.data?.data.assets || [];
	type ProductAssetData = typeof assetsOutput[number];
	const [openModal, setOpenModal] = useState<UploadModalModel | null>(null);

	const isLoading = assetsQuery.isLoading;
	const fault = assetsQuery.isError && assetsQuery.error;
	const assets = assetsQuery.isSuccess ? [...assetsOutput].sort(sortAssets) : [];

	const categoryLabel = category === assetConst.CATEGORY.MARKETING ?
		'marketing' :
		category === assetConst.CATEGORY.PACKAGING ?
			'packaging' :
			category === assetConst.CATEGORY.CODE_IN_BOX ?
				'code-in-box' :
				'';
	const buttonLabel = category === assetConst.CATEGORY.MARKETING ?
		'Upload Marketing Asset for Approval' :
		category === assetConst.CATEGORY.PACKAGING ?
			'Upload Packaging/Label Asset for Approval' :
			category === assetConst.CATEGORY.CODE_IN_BOX ?
				'Upload Code-In-Box Asset for Approval' :
				'';

	const hasUploadPermissions = !!product && canAddAssets(userProfile, product, category);

	const getAssetThumbnailUrl = (asset: ProductAssetData) =>
		asset.asset_thumbnail_file_id
			? isAuthorizedForPresignedUrl(userProfile.permissions)
				? `${global.config.apiUrl}/pdb/files/${asset.asset_thumbnail_file_id}?thumbnail=true`
				: `${global.config.sfsUrl}/files/${asset.asset_thumbnail_file_id}?thumbnail=true`
			: undefined;

	const showRequestToForgoAlert =
		category === assetConst.CATEGORY.PACKAGING &&
		assets.some(
			(asset) =>
				asset.asset_type === assetConst.TYPE.LABEL &&
				asset.final_label_forgo_status ===
					assetConst.FINAL_LABEL_FORGO_STATUS.REQUEST_FORGO,
		);

	const showCodeInBoxBanner =
		category === assetConst.CATEGORY.PACKAGING &&
		!readOnly &&
		!!product &&
		canAddAssets(userProfile, product, assetConst.CATEGORY.CODE_IN_BOX) &&
		hasUploadPermissions;

	const hasHelpTopicAccess = !!useReferenceData('helpTopics')?.length;
	const showHelpMenuTemplateBanner =
		!readOnly &&
		!!product &&
		(
			[
				assetConst.CATEGORY.CODE_IN_BOX,
				assetConst.CATEGORY.MARKETING,
				assetConst.CATEGORY.PACKAGING,
			] as ReadonlyArray<string>
		).includes(category) &&
		hasUploadPermissions &&
		hasHelpTopicAccess;
	
	return (
		<>
			{isLoading ? (
				<Loading />
			) : (
				<>
					{showRequestToForgoAlert && (
						<Alert className="alert-icon" variant="info">
							A request to Forgo Final Label Approval has been sent by Nintendo
						</Alert>
					)}
					{showCodeInBoxBanner && (
						<Alert variant="info">
							Do not use this tab to submit packaging for Code-in-Box releases. Please
							use the Code-In-Box Assets tab for accurate tracking.
						</Alert>
					)}
					{product.platform_code === 'BEE' &&
						category === assetConst.CATEGORY.CODE_IN_BOX && (
							<Alert className="alert-icon" variant="info">
								Please follow the instructions for Full Game Download (element 29)
								when creating the packaging.
							</Alert>
					)}
					{product.platform_code === 'NXS' &&
						category === assetConst.CATEGORY.PACKAGING && (
							<Alert className="alert-icon" variant="info">
								This product is a <b>Nintendo Switch 2 Enhanced</b> version of a{' '}
								<b>Nintendo Switch</b> product. Please include the
								Nintendo Switch 2 Enhanced (element 31) message on the
								packaging.
							</Alert>
					)}
					{product.platform_code === 'POT' &&
						category === assetConst.CATEGORY.PACKAGING && (
							<Alert className="alert-icon" variant="info">
								This is a <b>Nintendo Switch 2 Game Key Card</b> product. Please
								include the Key Card notices (element 30) on the packaging.
							</Alert>
					)}
					{/* superseded product alerts */}
					{category === assetConst.CATEGORY.PACKAGING &&
						product.platform_code === 'HAC' &&
						(product.superseded_override_flag === true ? (
							<Alert className="alert-icon" variant="info">
								An enhanced version of this product exists. A temporary override has
								been applied to allow packaging submissions for this{' '}
								<b>Nintendo Switch</b> version.
							</Alert>
						) : product.retail_status === 'SUPERSEDED' ? (
							<Alert className="alert-icon" variant="danger">
								This <b>Nintendo Switch</b> product has been superseded by a{' '}
								<b>Nintendo Switch 2 Enhanced</b> version. Please submit packaging
								using the Nintendo Switch 2 version.
							</Alert>
						) : product.related_products?.some(({ relation_type }) => relation_type === 'ENHANCED') ? (
							<Alert className="alert-icon" variant="warning">
								This <b>Nintendo Switch</b> product has been superseded by a{' '}
								<b>Nintendo Switch 2 Enhanced</b> version. Packaging submissions for
								the Nintendo Switch will not be supported after the Nintendo Switch
								2 Enhanced version has been ordered.
							</Alert>
						) : null)}
					{showHelpMenuTemplateBanner && (
						<Alert variant="info">
							Please use the Help Menu to access {categoryLabel} asset templates.
						</Alert>
					)}
					{hasUploadPermissions && (
						<Button
							type="button"
							onClick={(e) => {
								setOpenModal({
									type: 'ASSET_UPLOAD_MODAL',
									productId: product.product_id,
									category,
								});
							}}
							disabled={readOnly}
						>
							<FAIcon name="upload" className="mr-1" />
							{buttonLabel}
						</Button>
					)}
					{fault ? (
						<Alert className="mt-3" variant="danger">
							There was an error while loading {categoryLabel} assets data.{' '}
							<ActionLink onClick={() => assetsQuery.refetch()}>
								Retry load
							</ActionLink>
							<FAIcon name="chevron-right" size="xs" />
						</Alert>
					) : (
						<Table className="ProductProfile__asset-table mt-2" striped hover>
							<thead>
								<tr>
									<th style={{ width: '10rem' }}>Asset</th>
									<th>File(s)</th>
									<th style={{ width: '12%' }}>Type</th>
									<th style={{ width: '12%' }}>Asset Language(s)</th>
									<th style={{ width: '8rem' }}>Upload Date</th>
									<th style={{ width: '15%' }}>Status</th>
									<th style={{ width: '3rem' }}></th>
								</tr>
							</thead>
							{assets?.length ? (
								<tbody>
									{assets?.map((asset, index) => (
										<tr key={index}>
											<td>
												{asset.asset_thumbnail_file_id && (
													<img
														src={getAssetThumbnailUrl(asset)}
														alt="No preview available."
													/>
												)}
												{!asset.asset_thumbnail_file_id &&
													asset.most_recent_files && (
														<img
															src={asset_no_preview}
															alt="No preview available."
														/>
													)}
												{!asset.asset_thumbnail_file_id &&
													!asset.most_recent_files && (
														<FAIcon name="exclamation-triangle" />
													)}
											</td>
											<td>
												<ul>
													{asset.most_recent_files &&
														asset.most_recent_files.length > 0 &&
														asset.most_recent_files.map(
															(file, index) => (
																<li key={makeKey('li-', index)}>
																	{file.file_name}
																</li>
															),
														)}
												</ul>
											</td>
											<td>
												{formatAssetType(asset.asset_type)}
												{asset.asset_type_other_name
													? ' - ' + asset.asset_type_other_name
													: ''}
											</td>
											<td>
												<div>
													{asset.languages?.map((lang) => {
														return (
															<div key={makeKey('li-', lang)}>
																{formatLocale(lang)}
															</div>
														);
													})}
												</div>
											</td>
											<td>
												{formatDate(
													parseDateString(asset.submitted_date),
													dateFormat.DATE,
												)}
											</td>
											<td>
												<StatusText>
													{displayString(asset.review_status, [
														'DRI',
														'TV',
														'NCL',
													])}
												</StatusText>
											</td>
											<td>
												{asset.asset_id > 0 && (
													<Link
														to={
															'/products/' +
															product.product_id +
															'/assets/' +
															asset.asset_id
														}
													>
														<FAIcon name="chevron-right" />
													</Link>
												)}
											</td>
										</tr>
									))}
								</tbody>
							) : (
								<tbody>
									<tr>
										<td className="text-center" colSpan={7}>
											<i>No {categoryLabel} assets submitted</i>
										</td>
									</tr>
								</tbody>
							)}
						</Table>
					)}
					<AssetUploadModal
						show={openModal?.type === 'ASSET_UPLOAD_MODAL'}
						category={openModal?.category}
						productId={openModal?.productId}
						closeModal={() => setOpenModal({ ...openModal, type: undefined })}
						onSubmitComplete={() => assetsQuery.refetch()}
					/>
				</>
			)}
		</>
	);
};

export default AssetsTab;
